import type { ComponentChildren } from "preact";
import type { Field as FieldDef } from "../../api/types.gen.ts";
import { locale, t } from "../../i18n/index.ts";
import { optionLabel } from "../../lib/fieldTypes.ts";
import {
  formatAmount,
  formatDate,
  formatDuration,
  formatInstant,
  formatLocalDateTime,
  formatNumber,
  formatTime,
  formatZoned,
} from "../../lib/format.ts";
import { kindOf, mapUrl, pointOf, readField, showLocation, unitSymbol } from "../../lib/formats.ts";
import { formatDecimal, formatPercent } from "../../lib/number.ts";
import { clip } from "../../lib/text.ts";

const MAX_TEXT = 60;

const isMoney = (v: unknown): v is { amount: string; currency: string } =>
  typeof v === "object" &&
  v !== null &&
  typeof (v as Record<string, unknown>)["amount"] === "string" &&
  typeof (v as Record<string, unknown>)["currency"] === "string";

const isQuantity = (v: unknown): v is { amount: string; unit: string } =>
  typeof v === "object" &&
  v !== null &&
  typeof (v as Record<string, unknown>)["amount"] === "string" &&
  typeof (v as Record<string, unknown>)["unit"] === "string";

/** A zoned date-time in its zone, plus the viewer's local time when that differs. */
function zoned(s: string): string | null {
  const z = formatZoned(s);
  return z && (z.here ? `${z.there} ${t("fields.yourTime", { time: z.here })}` : z.there);
}

/** Temporal strings per their field type (null when the value doesn't parse, so it shows as stored). */
function temporal(type: string | undefined, s: string): string | null {
  switch (type) {
    case "date":
      return formatDate(s);
    case "time":
      return formatTime(s);
    case "local_datetime":
      return formatLocalDateTime(s);
    case "instant":
      return formatInstant(s);
    case "datetime":
      return zoned(s);
    case "duration":
      return formatDuration(s);
    default:
      return null;
  }
}

/** A link inside a row or a card: its own click, not the row's. */
function Out({ href, children, external }: { href: string; children: ComponentChildren; external?: boolean }) {
  return (
    <a
      href={href}
      class="text-blue-700 hover:underline"
      onClick={(e) => e.stopPropagation()}
      {...(external && { target: "_blank", rel: "noopener noreferrer" })}
    >
      {children}
    </a>
  );
}

/**
 * An email, phone, URL or formatted text: its display form, as a link where it is one (mailto:, tel: with its E.164,
 * the web address showing its domain); invalid, as written (dotted underline, its key's kind in the title).
 */
function typed(field: FieldDef, s: string, region: string | null | undefined): ComponentChildren | null {
  const kind = kindOf(field.type, field.format, field);
  if (!kind) {
    return null;
  }
  const r = readField(field, kind, s, field.region ?? region);
  if (!r?.valid) {
    return (
      <span
        class="underline decoration-amber-500 decoration-dotted underline-offset-2"
        title={t("fields.notValidShort")}
      >
        {clip(s, MAX_TEXT)}
      </span>
    );
  }
  switch (kind) {
    case "email":
      return <Out href={`mailto:${s.trim()}`}>{r.display}</Out>;
    case "phone":
      return <Out href={`tel:${r.key}`}>{r.display}</Out>;
    case "url":
      return (
        <Out href={r.key} external={true}>
          {r.display}
        </Out>
      );
    default:
      return r.display;
  }
}

/** Formats one value per its field type; anything that doesn't fit the type falls back to (truncated) JSON. */
function scalar(field: FieldDef | undefined, v: unknown, region: string | null | undefined): ComponentChildren {
  const type = field?.type;
  const when = typeof v === "string" ? temporal(type, v) : null;
  if (when !== null) {
    return when;
  }
  if (field && typeof v === "string") {
    const shown = typed(field, v, region);
    if (shown !== null) {
      return shown;
    }
  }
  if (type === "select" && typeof v === "string") {
    return optionLabel(field, v);
  }
  if (type === "number" && typeof v === "number") {
    return formatNumber(v);
  }
  if (type === "percentage" && typeof v === "number") {
    return formatPercent(v, locale.value);
  }
  if (type === "boolean" && typeof v === "boolean") {
    return v ? t("common.yes") : t("common.no");
  }
  if (type === "money" && isMoney(v)) {
    return formatAmount(v.amount, v.currency);
  }
  if (type === "quantity" && isQuantity(v)) {
    return `${formatDecimal(v.amount, locale.value)} ${unitSymbol(v.unit)}`;
  }
  const point = type === "location" ? pointOf(v) : null;
  if (point) {
    // Its coordinates (after its label), linking to the place on the map.
    const coords = showLocation(point);
    const shown = point.label === undefined ? coords : `${clip(point.label, MAX_TEXT)} (${coords})`;
    return (
      <Out href={mapUrl(point)} external={true}>
        {shown}
      </Out>
    );
  }
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return clip(s, MAX_TEXT);
}

/**
 * A data value shown per its field type (lists joined by commas). `region` is the space's: phones and plates read in
 * it when their field has none.
 */
export function Value({
  field,
  value,
  region,
}: {
  field: FieldDef | undefined;
  value: unknown;
  region?: string | null | undefined;
}) {
  if (value === undefined || value === null) {
    return <span class="text-zinc-400">—</span>;
  }
  const list = Array.isArray(value) && field?.many ? value : [value];
  return (
    <>
      {list.map((v, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: list positions are the identity of unkeyed values.
        <span key={i}>
          {i > 0 ? ", " : null}
          {scalar(field, v, region)}
        </span>
      ))}
    </>
  );
}
